import { useEffect, useMemo, useState } from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";
import {
  getCountFromServer,
  orderBy,
  query,
  where,
  limit,
} from "firebase/firestore";
import {
  getManuscriptProcessingReportingCollection,
  MANUSCRIPT_PROCESSING_STATUSES,
} from "integrations/firebase/collections/manuscriptProcessingReporting";
import { logError } from "shared/services/ErrorReporting";
import { usePagination } from "hooks/usePagination";

export const useManuscriptProcessingReports = ({
  organisationId,
}: {
  organisationId: string;
}) => {
  const [itemsCount, setItemsCount] = useState(0);
  const [countFailed, setCountFailed] = useState(false);
  const [statusCounts, setStatusCounts] = useState<
    Record<string, number | undefined>
  >({});
  const { page, pageSize } = usePagination();

  const baseQuery = useMemo(
    () =>
      query(
        getManuscriptProcessingReportingCollection(),
        where("organisation.id", "==", organisationId),
        orderBy("createdAt", "desc"),
      ),
    [organisationId],
  );

  const from = (page - 1) * pageSize;
  const to = from + pageSize;

  // One past the page: the only signal that another page exists when the count
  // request fails.
  const [reports = [], loading, error] = useCollectionData(
    query(baseQuery, limit(to + 1)),
  );

  useEffect(() => {
    if (error) logError(error);
  }, [error]);

  useEffect(() => {
    let active = true;
    const refreshCount = async () => {
      try {
        const snapshot = await getCountFromServer(baseQuery);
        const { count } = snapshot.data();
        if (active) {
          setItemsCount(count);
          setCountFailed(false);
        }
      } catch {
        if (active) setCountFailed(true);
      }
    };

    refreshCount();
    return () => {
      active = false;
    };
  }, [baseQuery]);

  useEffect(() => {
    let active = true;
    const refreshStatusCounts = async () => {
      const counted = await Promise.all(
        MANUSCRIPT_PROCESSING_STATUSES.map(async (status) => {
          try {
            const snapshot = await getCountFromServer(
              query(
                getManuscriptProcessingReportingCollection(),
                where("organisation.id", "==", organisationId),
                where("status", "==", status),
              ),
            );
            return [status, snapshot.data().count] as const;
          } catch {
            return [status, undefined] as const;
          }
        }),
      );

      if (active) setStatusCounts(Object.fromEntries(counted));
    };

    refreshStatusCounts();
    return () => {
      active = false;
    };
  }, [organisationId]);

  return {
    loading,
    // A paging reset empties reports, which in degraded mode would read as a
    // first paint and collapse the panel.
    isFirstLoad: loading && itemsCount === 0 && !countFailed,
    error,
    reports: reports.slice(from, to),
    // Zero would strand every page after the first behind a dead control.
    itemsCount: countFailed ? reports.length : itemsCount,
    statusCounts,
  };
};
